import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { X, Download, Link2, Check } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';
import MermaidBlock from './MermaidBlock';
import SplitBars, { SplitProvider } from './SplitBars';
import AgentAvatar from './AgentAvatar';
import UsageLine from './UsageLine';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { useDrawerOptional, TAB_BAR_WIDTH } from '../../components/DrawerSystem';

/**
 * The report of a thread of agent work, opened beside the chat drawer (to its
 * left), with the question, who took part, the Markdown report (Mermaid diagrams
 * render), what it cost, and a download as .md. Never a participant's voice.
 */
const Panel = styled.aside`
  position: fixed;
  top: 0;
  bottom: 0;
  right: ${(p) => p.right}px;
  width: min(560px, calc(100vw - ${(p) => p.right}px));
  z-index: 1190;
  display: flex;
  flex-direction: column;
  background: #141822;
  border-left: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: -12px 0 30px rgba(0, 0, 0, 0.35);
  color: #fff;
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Title = styled.div`
  flex: 1;
  min-width: 0;
  h2 { margin: 0 0 2px; font-size: 15px; line-height: 1.25; }
  p { margin: 0; font-size: 12px; color: rgba(255, 255, 255, 0.6); }
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: transparent;
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  &:hover { background: rgba(255, 255, 255, 0.08); }
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px 18px 24px;
  font-size: 13.5px;
  line-height: 1.5;
  h2 { font-size: 14px; margin: 18px 0 6px; }
  h3 { font-size: 13px; margin: 14px 0 4px; }
  ul, ol { padding-left: 20px; }
  li { margin: 3px 0; }
  pre { white-space: pre-wrap; }
`;

const Note = styled.p`
  margin: 0;
  padding: 8px 16px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.45);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const mdComponents = {
  code({ className, children, ...props }) {
    if (/language-mermaid/.test(className || '')) return <MermaidBlock chart={String(children).replace(/\n$/, '')} />;
    if (/language-kuutio-split/.test(className || '')) return <SplitBars body={String(children)} />;
    return <code className={className} {...props}>{children}</code>;
  },
  pre({ children }) {
    const child = React.Children.toArray(children)[0];
    if (/language-(mermaid|kuutio-split)/.test(child?.props?.className || '')) return child;
    return <pre>{children}</pre>;
  },
};

function download(thread, kindLabel) {
  const head = `# ${kindLabel}: ${thread.question}\n\n`;
  const blob = new Blob([head + (thread.report || '')], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(thread.kind || 'thread')}-${String(thread.id).slice(0, 8)}.md`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** `sources`: the thread's turns' sources, so a kuutio-split block's refs link to their numbers. The .md download keeps the fence as is. */
export default function ReportPanel({ thread, instanceId, onClose, sources = [] }) {
  const { t } = useTranslation();
  const drawer = useDrawerOptional();
  const [share, setShare] = useState({ state: 'idle', url: null, error: null });
  if (!thread) return null;

  // Publish the thread as a public artifact and put its link on the clipboard
  const publish = async () => {
    setShare({ state: 'working', url: null, error: null });
    try {
      const r = await fetch(API_ENDPOINTS.ARTIFACT_PUBLISH(instanceId), {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: 'thread', sourceId: thread.id, visibility: 'public' }),
      });
      const body = await r.json().catch(() => ({}));
      if (r.status === 401) { setShare({ state: 'error', url: null, error: t('chatAgent.thread.shareSignIn') }); return; }
      if (!r.ok) { setShare({ state: 'error', url: null, error: body.error || r.statusText }); return; }
      let copied = false;
      try { await navigator.clipboard.writeText(body.url); copied = true; } catch { /* shown below for copying by hand */ }
      setShare({ state: copied ? 'copied' : 'shared', url: body.url, error: null });
    } catch (e) {
      setShare({ state: 'error', url: null, error: e.message });
    }
  };
  const right = (drawer?.drawerWidthPx || 420) + TAB_BAR_WIDTH;
  const kindLabel = t(`chatAgent.thread.kind.${thread.kind || 'bounce'}`);
  // Through a portal on document.body: inside the drawer (position fixed, z-index 50) the
  // panel's own z-index counts only within the drawer, and the page header (z-index 100)
  // drew over its top (27.10.2026, K10 on local).
  return createPortal(
    <Panel right={right} role="dialog" aria-label={t('chatAgent.thread.reportTitle')}>
      <Head>
        <Title>
          <h2>{t('chatAgent.thread.reportTitle')}</h2>
          <p>{kindLabel}: {thread.question}</p>
        </Title>
        <IconBtn type="button" onClick={publish} disabled={share.state === 'working'} title={t('chatAgent.thread.shareTitle')}>
          {share.state === 'copied' ? <Check size={14} /> : <Link2 size={14} />} {t(share.state === 'copied' ? 'chatAgent.thread.shareCopied' : 'chatAgent.thread.share')}
        </IconBtn>
        <IconBtn type="button" onClick={() => download(thread, kindLabel)} title={t('chatAgent.thread.download')}>
          <Download size={14} /> .md
        </IconBtn>
        <IconBtn type="button" onClick={onClose} aria-label={t('chatAgent.thread.close')}><X size={14} /></IconBtn>
      </Head>
      {(share.url || share.error) && (
        <Meta style={{ fontSize: 12 }}>
          {share.url && <span><a href={share.url} target="_blank" rel="noreferrer" style={{ color: '#9fd8c2' }}>{share.url}</a> · {t('chatAgent.thread.sharePublic')}</span>}
          {share.error && <span style={{ color: '#ff9b9b' }}>{share.error}</span>}
        </Meta>
      )}
      <Meta>
        {(thread.participants || []).map((p) => (
          <span key={p.slug} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <AgentAvatar speaker={p} size={20} /> {p.name}
          </span>
        ))}
        {thread.usage && <UsageLine usage={thread.usage} />}
      </Meta>
      <Body>
        <SplitProvider sources={sources} tone="dark">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{thread.report || ''}</ReactMarkdown>
        </SplitProvider>
      </Body>
      <Note>{t('chatAgent.thread.reportDisclaimer')}</Note>
    </Panel>
  , document.body);
}
